import React, { useState, useEffect, useCallback } from "react";
import client from "@/api/client";
import { BarChart2, TrendingDown, Shield, Globe, Copy, CheckCheck,
         Download, AlertTriangle, CheckCircle2, Hash, ChevronDown, ChevronRight } from "lucide-react";

const CONF_COLORS = {
  high:      "text-emerald-700 bg-emerald-50 border-emerald-200",
  medium:    "text-blue-700 bg-blue-50 border-blue-200",
  low:       "text-amber-700 bg-amber-50 border-amber-200",
  very_low:  "text-red-700 bg-red-50 border-red-200",
};
const RISK_COLORS = {
  very_high: "text-red-700 bg-red-50 border border-red-200",
  high:      "text-orange-700 bg-orange-50 border border-orange-200",
  medium:    "text-amber-700 bg-amber-50 border border-amber-200",
  low:       "text-emerald-700 bg-emerald-50 border border-emerald-200",
  very_low:  "text-slate-600 bg-slate-50 border border-slate-200",
  unknown:   "text-slate-500 bg-slate-50 border border-slate-200",
};
const CAT_COLORS = {
  electricity: "bg-blue-500",
  fuel:        "bg-orange-500",
  transport:   "bg-violet-500",
  process:     "bg-rose-500",
  waste:       "bg-amber-500",
  materials:   "bg-teal-500",
  other:       "bg-slate-400",
};

function MetricCard({ label, value, unit, sub, accent = false }) {
  return (
    <div className={`rounded-xl p-4 border ${accent ? "bg-emerald-50 border-emerald-200" : "bg-white border-slate-200"}`}>
      <p className="text-xs uppercase tracking-wider text-slate-400 mb-1">{label}</p>
      <p className={`text-xl font-bold tabular-nums ${accent ? "text-[#059669]" : "text-[#0F172A]"}`}>{value}</p>
      {unit && <p className="text-xs text-slate-400 mt-0.5">{unit}</p>}
      {sub && <p className="text-xs text-slate-400 mt-0.5 italic">{sub}</p>}
    </div>
  );
}

function SectionHeader({ icon: Icon, title, badge }) {
  return (
    <div className="flex items-center gap-2 mb-3">
      <Icon size={15} className="text-[#059669]" />
      <h3 className="text-sm font-semibold text-[#0F172A]">{title}</h3>
      {badge && <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">{badge}</span>}
    </div>
  );
}

export default function BaselineSummary({ assessmentId }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [revenue, setRevenue] = useState("");
  const [expandedSection, setExpandedSection] = useState("emissions");

  const load = useCallback(async (revenueUsd = null) => {
    setLoading(true);
    setError(null);
    try {
      const q = revenueUsd ? `?annual_revenue_usd=${revenueUsd}` : "";
      const res = await client.get(`/baseline/assessments/${assessmentId}${q}`);
      setData(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to load baseline summary");
    }
    setLoading(false);
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  const copyJSON = () => {
    navigator.clipboard.writeText(JSON.stringify(data, null, 2));
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const downloadJSON = () => {
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `baseline-${assessmentId}-${data?.assessment?.reporting_year || "summary"}.json`;
    a.click();
  };

  const toggle = (id) => setExpandedSection(expandedSection === id ? null : id);

  if (loading) return (
    <div className="space-y-4">
      {[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-white border border-slate-200 rounded-xl animate-pulse" />)}
    </div>
  );

  if (error) return (
    <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex gap-3 text-red-600">
      <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
      <p className="text-sm">{error}</p>
    </div>
  );

  if (!data) return null;

  const { emissions, intensity, confidence, sector_classification: sector,
          methodology, data_completeness, activity_hotspots, v2_readiness,
          organization, reporting_period, assessment } = data;

  const byCategory = emissions?.category_breakdown || {};
  const hotspots = emissions?.hotspots || [];
  const totalTCO2e = emissions?.total_tco2e || 0;

  return (
    <div className="space-y-4 animate-fade-in" data-testid="baseline-summary">
      {/* Header + Actions */}
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-slate-400 mb-1">
            V1 Structured Output · Schema {data.schema_version}
          </p>
          <h2 className="text-base font-semibold text-[#0F172A]">Baseline Summary</h2>
          <p className="text-xs text-slate-400 mt-0.5">Standardized structured object for Financial Institution Module (V2) consumption</p>
        </div>
        <div className="flex items-center gap-2">
          {/* V2 ready badge */}
          <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold border ${v2_readiness?.ready ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            {v2_readiness?.ready ? <><CheckCircle2 size={12} /> V2 Ready</> : <><AlertTriangle size={12} /> V2 Gaps</>}
          </span>
          <button onClick={copyJSON} className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 text-xs font-medium text-slate-600 rounded-lg hover:bg-slate-50 transition-colors">
            {copied ? <><CheckCheck size={12} className="text-[#059669]" /> Copied</> : <><Copy size={12} /> Copy JSON</>}
          </button>
          <button onClick={downloadJSON} className="inline-flex items-center gap-1 px-3 py-1.5 bg-[#0F172A] text-white text-xs font-medium rounded-lg hover:bg-[#1E293B] transition-colors" data-testid="download-baseline-json">
            <Download size={12} /> Export JSON
          </button>
        </div>
      </div>

      {/* V2 Gaps */}
      {v2_readiness?.gaps?.length > 0 && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
          <p className="text-xs font-semibold text-amber-800 mb-2">V2 Readiness Gaps</p>
          {v2_readiness.gaps.map((gap, i) => (
            <div key={i} className="flex items-start gap-2 text-xs text-amber-700 mb-1">
              <AlertTriangle size={11} className="mt-0.5 flex-shrink-0" />
              {gap}
            </div>
          ))}
        </div>
      )}

      {/* Intensity Override */}
      <div className="bg-white border border-slate-200 rounded-xl p-4">
        <div className="flex items-center gap-4 flex-wrap">
          <p className="text-xs font-medium text-slate-600">Add revenue for intensity metrics:</p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-400">USD</span>
            <input
              type="number"
              value={revenue}
              onChange={e => setRevenue(e.target.value)}
              placeholder="Annual revenue"
              className="w-44 h-8 px-2 border border-slate-300 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#059669]"
            />
            <button onClick={() => load(revenue ? parseFloat(revenue) : null)}
              className="px-3 py-1.5 bg-[#059669] text-white text-xs font-medium rounded-lg hover:bg-emerald-700">
              Recalculate
            </button>
          </div>
        </div>
      </div>

      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <MetricCard label="Total Emissions" value={`${totalTCO2e.toFixed(2)}`} unit="tCO₂e" accent />
        <MetricCard label="Climate Score" value={`${emissions?.hotspots?.[0] ? confidence?.overall_score?.toFixed(1) : "—"}/100`} unit="Data quality weighted" />
        {intensity?.per_employee ? (
          <MetricCard label="Intensity / Employee" value={intensity.per_employee.value.toFixed(2)} unit={intensity.per_employee.unit} />
        ) : (
          <MetricCard label="Intensity / Employee" value="—" unit="Add employee count" />
        )}
        {intensity?.per_usd_million_revenue ? (
          <MetricCard label="Intensity / $M Revenue" value={intensity.per_usd_million_revenue.value.toFixed(2)} unit="tCO₂e / $M" />
        ) : (
          <MetricCard label="Intensity / Revenue" value="—" unit="Add annual revenue above" />
        )}
      </div>

      {/* Emissions Breakdown ── collapsible */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <button onClick={() => toggle("emissions")} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-2">
            <BarChart2 size={15} className="text-[#059669]" />
            <span className="text-sm font-semibold text-[#0F172A]">Emissions by Category</span>
            <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">Standard 6</span>
          </div>
          {expandedSection === "emissions" ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </button>
        {expandedSection === "emissions" && (
          <div className="px-5 pb-5">
            {/* Bar chart (CSS) */}
            <div className="space-y-2.5 mb-5">
              {Object.entries(byCategory).filter(([, v]) => v.tco2e > 0).sort((a, b) => b[1].tco2e - a[1].tco2e).map(([cat, v]) => (
                <div key={cat}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <div className={`w-2.5 h-2.5 rounded-sm ${CAT_COLORS[cat] || "bg-slate-400"}`} />
                      <span className="text-xs font-medium text-[#0F172A]">{v.label}</span>
                      <span className="text-xs text-slate-400">Scope {v.scope}</span>
                      {v.is_material && <span className="text-xs px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded text-[10px] font-medium">Material</span>}
                    </div>
                    <span className="text-xs font-bold tabular-nums text-[#0F172A]">{v.tco2e.toFixed(3)} t <span className="text-slate-400 font-normal">({v.pct_total}%)</span></span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className={`h-full rounded-full ${CAT_COLORS[cat] || "bg-slate-400"}`} style={{ width: `${v.pct_total}%` }} />
                  </div>
                </div>
              ))}
            </div>

            {/* Scope breakdown */}
            <div className="grid grid-cols-3 gap-4">
              {Object.entries(emissions?.scope_breakdown || {}).map(([scope, sv]) => (
                <div key={scope} className="p-3 bg-slate-50 border border-slate-100 rounded-xl">
                  <p className="text-xs text-slate-400 mb-0.5 uppercase tracking-wider">{scope.replace("_", " ")}</p>
                  <p className="text-base font-bold tabular-nums text-[#0F172A]">{sv.tco2e.toFixed(2)}</p>
                  <p className="text-xs text-slate-400">tCO₂e · {sv.pct_total}%</p>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Intensity + Benchmarking */}
      {(data.intensity || data.benchmarking) && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button onClick={() => toggle("benchmarking")} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-2">
              <TrendingDown size={15} className="text-[#059669]" />
              <span className="text-sm font-semibold text-[#0F172A]">Intensity & Sector Benchmarking</span>
              {data.benchmarking?.per_employee?.position && (
                <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${
                  data.benchmarking.per_employee.position === "better_than_sector" ? "bg-emerald-50 text-emerald-700 border-emerald-200" :
                  data.benchmarking.per_employee.position === "near_sector" ? "bg-amber-50 text-amber-700 border-amber-200" :
                  "bg-red-50 text-red-700 border-red-200"
                }`}>
                  {data.benchmarking.per_employee.delta_pct > 0 ? "+" : ""}{data.benchmarking.per_employee.delta_pct?.toFixed(0)}% vs sector
                </span>
              )}
            </div>
            {expandedSection === "benchmarking" ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
          </button>
          {expandedSection === "benchmarking" && (
            <div className="px-5 pb-5 space-y-4">
              {/* Intensity */}
              {data.intensity && Object.keys(data.intensity).length > 0 && (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-400 mb-2">Emissions Intensity</p>
                  <div className="grid grid-cols-2 gap-4">
                    {Object.entries(data.intensity).map(([key, metric]) => (
                      <div key={key} className="p-3 bg-slate-50 border border-slate-100 rounded-lg">
                        <p className="text-lg font-bold tabular-nums text-[#0F172A]">{metric.value?.toFixed(3)}</p>
                        <p className="text-xs text-slate-400 mt-0.5">{metric.unit}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              {/* Benchmark comparison */}
              {data.benchmarking?.per_employee && (() => {
                const bm = data.benchmarking.per_employee;
                const isGreen = bm.position === "better_than_sector";
                const isRed = bm.position === "worse_than_sector";
                const barColor = isGreen ? "#059669" : isRed ? "#EF4444" : "#F59E0B";
                const benchPct = bm.benchmark_value ? Math.min(100, (bm.org_value / bm.benchmark_value) * 100) : 0;
                const benchRef = 100; // benchmark = 100%
                return (
                  <div className={`p-4 rounded-xl border ${isGreen ? "bg-emerald-50 border-emerald-200" : isRed ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">vs. Africa Sector Average</p>
                        <p className="text-sm font-semibold text-[#0F172A]">
                          {data.sector_classification?.category} · {data.benchmarking.benchmark_geography}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-bold tabular-nums" style={{ color: barColor }}>
                          {bm.delta_pct > 0 ? "+" : ""}{bm.delta_pct?.toFixed(0)}%
                        </p>
                        <p className="text-xs text-slate-500">{bm.position_label}</p>
                      </div>
                    </div>
                    {/* Comparison bars */}
                    <div className="space-y-2 mb-3">
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-600">Organization</span>
                          <span className="font-bold">{bm.org_value?.toFixed(2)} tCO₂e/emp</span>
                        </div>
                        <div className="h-2 bg-white/60 rounded-full overflow-hidden">
                          <div className="h-full rounded-full" style={{ width: `${Math.min(100, benchPct)}%`, backgroundColor: barColor }} />
                        </div>
                      </div>
                      <div>
                        <div className="flex justify-between text-xs mb-1">
                          <span className="text-slate-600">Sector Benchmark</span>
                          <span className="font-bold">{bm.benchmark_value?.toFixed(2)} tCO₂e/emp</span>
                        </div>
                        <div className="h-2 bg-white/60 rounded-full overflow-hidden">
                          <div className="h-full bg-slate-400 rounded-full" style={{ width: "100%" }} />
                        </div>
                      </div>
                    </div>
                    <p className="text-xs text-slate-600 leading-relaxed italic">{bm.interpretation}</p>
                    <p className="text-xs text-slate-400 mt-1">Source: {data.benchmarking.benchmark_source} ({data.benchmarking.benchmark_year})</p>
                  </div>
                );
              })()}
              {(!data.benchmarking || !data.benchmarking.per_employee) && (
                <p className="text-xs text-slate-400 italic">Add employee count to organization profile to enable sector benchmarking.</p>
              )}
              <p className="text-xs text-slate-400 p-3 bg-slate-50 rounded-lg">
                Benchmarks are based on Africa regional data. Add revenue via the recalculate button above for revenue-based intensity benchmarking.
              </p>
            </div>
          )}
        </div>
      )}
      {activity_hotspots?.length > 0 && (
        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <button onClick={() => toggle("hotspots")} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors">
            <div className="flex items-center gap-2">
              <TrendingDown size={15} className="text-[#059669]" />
              <span className="text-sm font-semibold text-[#0F172A]">Activity-Level Hotspot Mapping</span>
              <span className="text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">{activity_hotspots.length} records</span>
            </div>
            {expandedSection === "hotspots" ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
          </button>
          {expandedSection === "hotspots" && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">#</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Category</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider hidden sm:table-cell">Description</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">tCO₂e</th>
                    <th className="text-right px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">%</th>
                    <th className="text-left px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Factor</th>
                    <th className="text-center px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider hidden md:table-cell">Scope</th>
                    <th className="text-center px-4 py-2.5 font-semibold text-slate-500 uppercase tracking-wider">Hotspot</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {activity_hotspots.map((h, i) => (
                    <tr key={i} className={`hover:bg-slate-50 ${h.is_hotspot ? "bg-amber-50/30" : ""}`}>
                      <td className="px-4 py-2.5 text-slate-400 font-mono">{h.rank}</td>
                      <td className="px-4 py-2.5">
                        <div className="flex items-center gap-1.5">
                          <div className={`w-2 h-2 rounded-sm ${CAT_COLORS[h.category] || "bg-slate-300"}`} />
                          <span className="capitalize font-medium text-[#0F172A]">{h.category_label || h.category}</span>
                        </div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-500 hidden sm:table-cell max-w-36 truncate">{h.description || "—"}</td>
                      <td className="px-4 py-2.5 text-right font-bold tabular-nums text-[#0F172A]">{h.tco2e?.toFixed(4)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">{h.pct_total?.toFixed(1)}%</td>
                      <td className="px-4 py-2.5 text-slate-400 font-mono hidden md:table-cell text-[10px]">{h.factor_value} {h.factor_unit}</td>
                      <td className="px-4 py-2.5 text-center hidden md:table-cell">
                        <span className="text-xs px-1.5 py-0.5 bg-slate-100 text-slate-600 rounded">S{h.scope}</span>
                      </td>
                      <td className="px-4 py-2.5 text-center">
                        {h.is_hotspot ? (
                          <span className="text-xs px-2 py-0.5 bg-amber-50 text-amber-700 border border-amber-200 rounded-full font-medium">Top {h.rank}</span>
                        ) : <span className="text-slate-300">—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Confidence Breakdown */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <button onClick={() => toggle("confidence")} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-2">
            <Shield size={15} className="text-[#059669]" />
            <span className="text-sm font-semibold text-[#0F172A]">Confidence Score Breakdown</span>
            {confidence?.classification && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${CONF_COLORS[confidence.classification] || "bg-slate-100 text-slate-600"}`}>
                {confidence.overall_score?.toFixed(1)}% — {confidence.classification?.toUpperCase()}
              </span>
            )}
          </div>
          {expandedSection === "confidence" ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </button>
        {expandedSection === "confidence" && (
          <div className="px-5 pb-5 space-y-3">
            <p className="text-xs text-slate-500 leading-relaxed italic">{confidence?.interpretability}</p>
            {Object.entries(confidence?.components || {}).map(([key, comp]) => (
              <div key={key}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-semibold text-[#0F172A]">{comp.label}</span>
                    <span className="text-xs text-slate-400">Weight: {comp.weight_pct}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-[#059669] font-bold">{comp.score?.toFixed(1)}%</span>
                    <span className="text-xs text-slate-400 tabular-nums">→ {comp.contribution?.toFixed(1)}pts</span>
                  </div>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-[#059669] rounded-full" style={{ width: `${comp.score || 0}%` }} />
                </div>
              </div>
            ))}
            <div className="flex justify-between text-xs pt-2 border-t border-slate-100">
              <span className="font-semibold text-slate-600">Weighted Total</span>
              <span className="font-bold text-[#059669]">{confidence?.weighted_total?.toFixed(1)}%</span>
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-100">
              <p className="text-xs font-semibold text-slate-600 mb-1">Classification thresholds</p>
              <div className="flex gap-4 flex-wrap">
                {Object.entries(confidence?.classification_thresholds || {}).map(([k, v]) => (
                  <span key={k} className="text-xs text-slate-500"><strong className="capitalize">{k}</strong>: {v}</span>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Sector Classification */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <button onClick={() => toggle("sector")} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-2">
            <Globe size={15} className="text-[#059669]" />
            <span className="text-sm font-semibold text-[#0F172A]">Sector Classification</span>
            {sector?.ghg_risk_level && (
              <span className={`text-xs px-2 py-0.5 rounded-full font-semibold ${RISK_COLORS[sector.ghg_risk_level] || ""}`}>
                GHG Risk: {sector.ghg_risk_level.replace("_", " ")}
              </span>
            )}
          </div>
          {expandedSection === "sector" ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </button>
        {expandedSection === "sector" && (
          <div className="px-5 pb-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
            {[
              { label: "Sector (Raw)", value: sector?.primary || "—" },
              { label: "ISIC Code", value: sector?.isic_code || "—" },
              { label: "ISIC Description", value: sector?.isic_description || "—" },
              { label: "Category", value: sector?.category || "—" },
              { label: "GHG Risk Level", value: sector?.ghg_risk_level?.replace("_", " ") || "—" },
              { label: "Typical Drivers", value: (sector?.typical_emission_drivers || []).join(", ") || "—" },
              { label: "Scope 1 Profile", value: sector?.scope_1_intensity_profile?.replace("_", " ") || "—" },
              { label: "Scope 2 Profile", value: sector?.scope_2_intensity_profile?.replace("_", " ") || "—" },
            ].map(({ label, value }) => (
              <div key={label}>
                <p className="text-xs text-slate-400 uppercase tracking-wider mb-0.5">{label}</p>
                <p className="text-xs font-semibold text-[#0F172A] capitalize">{value}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Methodology Reference */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <button onClick={() => toggle("methodology")} className="w-full flex items-center justify-between px-5 py-4 hover:bg-slate-50 transition-colors">
          <div className="flex items-center gap-2">
            <Hash size={15} className="text-[#059669]" />
            <span className="text-sm font-semibold text-[#0F172A]">Methodology Reference</span>
          </div>
          {expandedSection === "methodology" ? <ChevronDown size={14} className="text-slate-400" /> : <ChevronRight size={14} className="text-slate-400" />}
        </button>
        {expandedSection === "methodology" && (
          <div className="px-5 pb-5 space-y-2">
            {[
              ["Standard", `${methodology?.name} v${methodology?.version}`],
              ["Reference", methodology?.standard],
              ["Calculation Hash (SHA-256)", methodology?.calculation_hash],
              ["Factors Used", `${methodology?.factors_used_count} approved factor(s)`],
              ["Sources", (methodology?.factor_sources || []).join(", ") || "—"],
              ["Calculated At", methodology?.calculated_at?.substring(0, 19).replace("T", " ") + " UTC" || "—"],
            ].map(([label, value]) => (
              <div key={label} className="flex items-start gap-3">
                <span className="text-xs text-slate-400 w-44 flex-shrink-0">{label}</span>
                <span className={`text-xs font-medium ${label.includes("Hash") ? "font-mono text-[#059669]" : "text-[#0F172A]"}`}>{value || "—"}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Data Completeness */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <SectionHeader icon={CheckCircle2} title="Data Completeness Report" />
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: "Total Activities", value: data_completeness?.total_activities },
            { label: "Matched to Factors", value: data_completeness?.matched_with_factors },
            { label: "Evidence Verified", value: data_completeness?.evidence_verified },
            { label: "Missing Required", value: data_completeness?.missing_required_categories?.length || 0, alert: (data_completeness?.missing_required_categories?.length || 0) > 0 },
          ].map(({ label, value, alert }) => (
            <div key={label} className={`p-3 rounded-lg border ${alert ? "bg-red-50 border-red-200" : "bg-slate-50 border-slate-100"}`}>
              <p className="text-xs text-slate-400 mb-0.5">{label}</p>
              <p className={`text-lg font-bold tabular-nums ${alert ? "text-red-600" : "text-[#0F172A]"}`}>{value}</p>
            </div>
          ))}
        </div>
        {data_completeness?.missing_required_categories?.length > 0 && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700">
            Missing required categories: <strong>{data_completeness.missing_required_categories.join(", ")}</strong>
          </div>
        )}
      </div>
    </div>
  );
}
